import { Component, DestroyRef, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { API, DeviceFullDTO, DeviceGeoStatsDTO } from 'src/app/services/api/api';
import { ErrorService } from 'src/app/services/error.service';
import { UtilService } from 'src/app/services/util.service';
import { GeoJsonObject, LineString, Point, Polygon } from 'geojson';
import * as L from 'leaflet';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-device-details-geo-stats',
  templateUrl: './device-details-geo-stats.component.html',
  styleUrls: ['./device-details-geo-stats.component.scss'],
  standalone: false,
})
export class DeviceDetailsGeoStatsComponent implements OnInit {
  private deviceId: number;
  public device: DeviceFullDTO;

  geoStatsloading: boolean = false;
  loading: boolean = true;

  geoStats: DeviceGeoStatsDTO = null;

  center: Point;

  points: Point[] = [];
  lineStrings: LineString[] = [];
  polygons: Polygon[] = [];
  layers: L.Layer[] = [];

  constructor(
    private api: API,
    private activatedRoute: ActivatedRoute,
    public util: UtilService,
    public errorHandler: ErrorService,
    private destroyRef: DestroyRef
  ) {}

  ngOnInit() {
    this.activatedRoute.parent.params.subscribe(params => {
      this.deviceId = +params.deviceID;
      this.refresh();
    });
  }

  refresh() {
    this.loading = true;
    this.api
      .ss_nm_Device_retrieveFullById(this.deviceId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (e: DeviceFullDTO) => {
          this.device = e;
          if (this.device.position) {
            this.center = this.device.position.data;
          }
          this.geoStatsRequest();
        },
        error: error => {
          if (!error.status || !(+error.status === 403 || +error.status === 404)) {
            this.errorHandler.errorHandler(error);
          }
        },
      });
  }

  geoStatsRequest() {
    this.geoStatsloading = true;

    this.api
      .ss_nm_Device_retrieveDeviceGeoStats(this.device.id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.geoStatsloading = false;
        })
      )
      .subscribe({
        next: (e: DeviceGeoStatsDTO) => {
          this.geoStats = e;

          const layers: L.Layer[] = [];
          const points: Point[] = [];
          const lineStrings: LineString[] = [];
          const polygons: Polygon[] = [];

          if (this.device.currentPosition) {
            layers.push(this.toLayer(this.device.currentPosition.data, { label: 'Current Position', color: 'blue' }));
          }

          for (const dto of e.roms) {
            layers.push(this.toLayer(dto.simple, { label: 'Range of Movement', color: 'grey' }));
          }
          if (e.pos) {
            layers.push(this.toLayer(e.pos.simple, { label: 'Perimeter of Service', color: 'yellow' }));
          }
          for (const dto of e.pobs) {
            layers.push(this.toLayer(dto.simple, { label: 'Perimeter of Business', color: 'green' }));
          }
          for (const dto of e.lobs) {
            layers.push(this.toLayer(dto.simple, { label: 'Loss of Business', color: 'red' }));
          }

          if (this.device.whitelistArea) {
            const whitelistArea = this.device.whitelistArea;
            for (const dto of whitelistArea.points) {
              layers.push(this.toLayer(dto.data, { label: 'Whitelist Area', color: 'white' }));
            }
            for (const dto of whitelistArea.lineStrings) {
              layers.push(this.toLayer(dto.simple, { label: 'Whitelist Area', color: 'white' }));
            }
            for (const dto of whitelistArea.polygons) {
              layers.push(this.toLayer(dto.simple, { label: 'Whitelist Area', color: 'white' }));
            }
          }

          this.points = points;
          this.lineStrings = lineStrings;
          this.polygons = polygons;
          this.layers = layers;
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  toLayer(
    data: GeoJsonObject,
    options: {
      label?: string;
      color?: string;
      fillColor?: string;
    }
  ): L.Layer {
    const layer: L.LayerGroup = new L.LayerGroup();
    let obj = L.geoJSON(data);
    const _options: any = {};
    if (options) {
      if (options.color) {
        _options.color = options.color;
      }
      if (options.fillColor) {
        _options.fillColor = options.fillColor;
      }
    }
    obj = obj.setStyle(_options);
    if (options.label) {
      obj = obj.bindTooltip(options.label, { permanent: true, direction: 'center' }).openTooltip();
    }
    obj.addTo(layer);
    return layer;
  }

  showMap() {
    /* keep empty due to map functionality*/
  }
}
